import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBreathingExercise, type BreathingPhase } from "@/hooks/useBreathingExercise";
import { usePrefersReducedMotion } from "@/components/accessibility/AccessibilityProvider";
import BreathingCircle from "./BreathingCircle";
import BreathingCycleProgress from "./BreathingCycleProgress";

interface Props {
  open: boolean;
  onClose: () => void;
}

const PATTERN: [number, number, number, number] = [4, 4, 4, 4];
const TOTAL_CYCLES = 5;

const phaseGuidance: Record<BreathingPhase, string> = {
  inhale: "Breathe in slowly through your nose…",
  hold: "Hold gently, feel the stillness…",
  exhale: "Release softly through your mouth…",
  rest: "Rest. Let your mind clear…",
};

export default function BreathingModal({ open, onClose }: Props) {
  const reduceMotion = usePrefersReducedMotion();
  const {
    phase,
    phaseCountdown,
    currentCycle,
    totalCycles,
    isRunning,
    isComplete,
    start,
    pause,
    reset,
  } = useBreathingExercise({ cycles: TOTAL_CYCLES, pattern: PATTERN });

  const modalRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Reset state whenever modal closes
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Escape key + focus trap
  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKey);
    // Focus the close button initially
    setTimeout(() => closeBtnRef.current?.focus(), 50);

    // Lock background scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="breathing-modal-title"
            className="relative w-full max-w-[400px] bg-white overflow-hidden"
            style={{
              borderRadius: 24,
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top accent line */}
            <div style={{ height: 3, backgroundColor: "#6B9E7A" }} aria-hidden="true" />

            <div className="p-10">
              {/* Close button */}
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                aria-label="Close breathing exercise"
                className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2"
                style={{ ["--tw-ring-color" as never]: "#2D5A3D" }}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <h2
                  id="breathing-modal-title"
                  className="text-2xl font-semibold mb-1.5"
                  style={{ color: "#1A1A1A", fontFamily: "ui-rounded, system-ui, sans-serif" }}
                >
                  Box Breathing
                </h2>
                <p className="text-sm" style={{ color: "#6B7280" }}>
                  5 cycles · ~2 minutes · Reduces stress instantly
                </p>
              </div>

              {/* Animated circle */}
              <div className="my-6">
                <BreathingCircle
                  phase={phase}
                  countdown={phaseCountdown}
                  isRunning={isRunning}
                  isComplete={isComplete}
                  reduceMotion={reduceMotion}
                  durations={{
                    inhale: PATTERN[0],
                    hold: PATTERN[1],
                    exhale: PATTERN[2],
                    rest: PATTERN[3],
                  }}
                />
              </div>

              {/* Cycle progress dots */}
              <div className="mb-2">
                <BreathingCycleProgress
                  total={totalCycles}
                  current={currentCycle}
                  isComplete={isComplete}
                />
              </div>

              {/* Cycle counter */}
              <p
                className="text-center text-[13px] mb-3"
                style={{ color: "#6B7280" }}
                aria-live="polite"
              >
                {isComplete ? `Completed ${totalCycles} of ${totalCycles}` : `Cycle ${currentCycle} of ${totalCycles}`}
              </p>

              {/* Guidance text */}
              <div className="min-h-[44px] flex items-center justify-center mb-5">
                <AnimatePresence mode="wait">
                  {isComplete ? (
                    <motion.p
                      key="complete-text"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center text-sm px-2"
                      style={{ color: "#2D5A3D" }}
                    >
                      Well done. Take a moment to notice how you feel.
                    </motion.p>
                  ) : (
                    <motion.p
                      key={phase}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.35 }}
                      className="text-center text-sm italic px-2"
                      style={{ color: "#6B7280" }}
                    >
                      {isRunning ? phaseGuidance[phase] : "Find a comfortable position. Press Start when you're ready."}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-3">
                {isComplete ? (
                  <>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 py-2.5 rounded-full text-sm font-medium border transition-colors"
                      style={{
                        borderColor: "rgba(45, 90, 61, 0.2)",
                        color: "#2D5A3D",
                        backgroundColor: "transparent",
                      }}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={start}
                      className="px-5 py-2.5 rounded-full text-sm font-medium text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: "#2D5A3D" }}
                    >
                      Repeat session
                    </button>
                  </>
                ) : !isRunning ? (
                  <button
                    type="button"
                    onClick={start}
                    className="px-8 py-2.5 rounded-full text-sm font-medium text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{ backgroundColor: "#2D5A3D" }}
                  >
                    {currentCycle > 1 || phase !== "inhale" ? "Resume" : "Start"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={pause}
                    className="px-8 py-2.5 rounded-full text-sm font-medium transition-colors border hover:bg-gray-50"
                    style={{
                      borderColor: "rgba(45, 90, 61, 0.3)",
                      color: "#2D5A3D",
                    }}
                  >
                    Pause
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
